import React, { ReactNode, useContext, useState } from 'react';
import UTEOTPInput from 'src/components/ute-otp-input';
import { IOTPValidity, OTPValidityDefault } from 'src/components/ute-otp-input/ute-otp-input-types';
import { AuthContext } from 'src/context/ute-auth-context/auth-provider';
import { IGenerateOTPResponse } from '../login/login-component/login-component-types';
import { LOGIN_CONSTANTS, RECOVERY_CONSTANTS } from 'src/helper/constants';
import { UTENotifier } from 'src/modules/notification/ute-notifier';
import { OTPValidationStatus } from 'src/modules/common/common-types';
import UTEGenerateOTP from 'src/components/ute-generate-otp';
import { IValidateOTPResponse } from './third-party-login-component-types';
import { useNavigate } from 'react-router-dom';
import { generateOTPForAU } from './third-party-login-component-api';

interface IThirdPartyLoginComponentProps {
	auClientGuid: string;
	validateOTP: (
		companyId: number,
		otp: string,
		auClientGuid: string,
		callback: (validateOTPResponse: IValidateOTPResponse) => void,
	) => void;
	component: string;
	generateOTPSubHeaderText?: ReactNode;
}

const ThirdPartyLoginComponent: React.FC<IThirdPartyLoginComponentProps> = ({
	auClientGuid,
	validateOTP,
	component,
	generateOTPSubHeaderText,
}) => {
	const { companyId, updateAuthToken, updateRefreshToken, updateUserDetails } = useContext(AuthContext);
	const [otp, setOTP] = useState('');
	const [otpValidity, setOTPValidity] = useState<IOTPValidity>(OTPValidityDefault);
	const [isOTPGenerated, setIsOTPGenerated] = useState<boolean>(false);
	const navigate = useNavigate();

	const updateLoggedInUserFlag = () => {
		localStorage.setItem(RECOVERY_CONSTANTS.UTE_LOGGEDIN_USER_FLAG, 'true');
	};

	const handleOTPSubmit = (callback: () => void) => {
		validateOTP(companyId, otp, auClientGuid, (validateOTPResponse: IValidateOTPResponse) => {
			if (validateOTPResponse.validOTPDetail.status === OTPValidationStatus.Success) {
				const { accessToken, refreshToken, data } = validateOTPResponse.validOTPDetail;
				localStorage.removeItem('sessionEndTime');
				if (component === 'login') {
					updateAuthToken(accessToken, data);
					updateRefreshToken(refreshToken);
					updateUserDetails(data);
					updateLoggedInUserFlag();
					navigate('/');
				} else if (component === 'access-verify') {
					navigate(
						`/third-party/access-verified?clientName=${encodeURIComponent(
							validateOTPResponse.validOTPDetail.data?.name,
						)}&isAlreadyVerified=false&accessIdentifier=${auClientGuid}`,
					);
				}
			} else if (validateOTPResponse.validOTPDetail.status === OTPValidationStatus.OTPExpired) {
				UTENotifier.Error(LOGIN_CONSTANTS.OTP_EXPIRED);
				setOTP('');
				callback();
			} else if (validateOTPResponse.validOTPDetail.status === OTPValidationStatus.InvalidOTP) {
				setOTPValidity({
					isValid: false,
					message: 'Invalid access code.',
				});
				setOTP('');
				callback();
			}
		});
	};

	const requestOTP = () => {
		generateOTPForAU(companyId, auClientGuid, (generateOTPResponse: IGenerateOTPResponse) => {
			if (generateOTPResponse.error) {
				UTENotifier.Error(LOGIN_CONSTANTS.ERROR_SENDING_OTP);
			} else {
				UTENotifier.Success(LOGIN_CONSTANTS.OTP_SENT);
			}
		});
	};

	const handleRequestOTP = () => {
		setIsOTPGenerated(true);
		requestOTP();
	};

	return (
		<>
			{!isOTPGenerated && (
				<UTEGenerateOTP
					subHeaderText={generateOTPSubHeaderText}
					handleOnClickEvent={handleRequestOTP}
				/>
			)}
			{isOTPGenerated && (
				<UTEOTPInput
					otpLength={6}
					otp={otp}
					setOTP={setOTP}
					otpValidity={otpValidity}
					setOTPValidity={setOTPValidity}
					handleOTPSubmit={handleOTPSubmit}
					handleResendCode={() => {}}
					resendCodeText={''}
					subHeaderText={
						<>
							A One-Time Access Code has been sent to
							<br />
							your mobile number; it{' '}
						</>
					}
					codeNotReceivedText={
						<>
							If you haven’t received the code yet or it is not working, <br />
							you can request a new one. <span onClick={requestOTP}>Resend Code</span>
						</>
					}
				/>
			)}
		</>
	);
};

export default ThirdPartyLoginComponent;
